import { useEffect, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Send, Loader2, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "What should I wear to a rooftop party?",
  "Build me a clean monochrome fit.",
  "How do I style white sneakers for work?",
  "What's missing from my wardrobe?",
];

export default function Stylist() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/stylist/messages")
      .then((r) => setMessages(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/stylist/chat", { message: msg });
      setMessages((p) => [...p, data]);
    } catch (e) {
      setMessages((p) => [...p, { role: "assistant", content: "I'm having a moment — try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-bg0 min-h-screen flex flex-col">
      <Navbar />
      <section className="max-w-[900px] w-full mx-auto px-6 md:px-10 py-10 flex-1 flex flex-col">
        <div className="mb-6">
          <div className="overline mb-3 text-volt">Your Personal Stylist</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl font-bold tracking-tighter">Eternal</h1>
        </div>

        <div className="flex-1 ep-card p-6 flex flex-col min-h-[52vh]" data-testid="stylist-chat">
          <div className="flex-1 overflow-y-auto space-y-5 pr-1">
            {loaded && messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="stylist-empty">
                <Sparkles size={30} className="text-volt" />
                <p className="font-display uppercase text-2xl font-semibold tracking-tight mt-4">
                  Hey{user?.name ? ` ${user.name.split(" ")[0]}` : ""}, let's build a fit.
                </p>
                <p className="text-epsub text-sm mt-2 max-w-sm">
                  Ask me anything — outfits, colours, what to cop next. I know your wardrobe.
                </p>
                <div className="flex flex-wrap gap-2 justify-center mt-6">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`suggestion-${i}`}
                      className="ep-btn text-xs border border-white/20 px-3 py-2 hover:border-volt hover:text-volt"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-volt text-black" : "bg-bg2 text-white/90 border border-epborder"
                }`}>
                  {m.role === "assistant" && <div className="overline mb-1 text-volt">Eternal</div>}
                  {m.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start" data-testid="stylist-typing">
                <div className="bg-bg2 border border-epborder px-4 py-3 flex items-center gap-2 text-epsub text-sm">
                  <Loader2 size={14} className="animate-spin text-volt" /> styling…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="flex items-center gap-3 mt-5 border-t border-epborder pt-5">
            <input
              value={input}
              type="text"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask your stylist…"
              data-testid="stylist-input"
              className="flex-1 bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 text-white placeholder:text-zinc-600 transition-colors"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              data-testid="stylist-send"
              className="ep-btn bg-volt text-black p-3 hover:bg-volt-hover disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
